/**
 * StreamErrorToast
 *
 * Amber auto-dismissing banner shown when the STT streaming pipeline
 * encounters an error (stt:stream_error WS message). Auto-dismisses after 5s.
 */

import { useEffect } from 'react';

interface StreamErrorToastProps {
  message: string | null;
  onDismiss: () => void;
}

export function StreamErrorToast({ message, onDismiss }: StreamErrorToastProps) {
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(onDismiss, 5_000);
    return () => clearTimeout(timer);
  }, [message, onDismiss]);

  if (!message) return null;

  return (
    <div
      role="alert"
      className="flex-shrink-0 bg-amber-50 border-b border-amber-200 px-4 py-2"
    >
      <div className="max-w-2xl mx-auto flex items-center gap-2">
        {/* Warning triangle icon */}
        <svg
          className="w-4 h-4 flex-shrink-0 text-safety-amber"
          viewBox="0 0 16 16"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6.457 1.047c.659-1.234 2.427-1.234 3.086 0l6.082 11.378A1.75 1.75 0 0 1 14.082 15H1.918a1.75 1.75 0 0 1-1.543-2.575Zm1.763.707a.25.25 0 0 0-.44 0L1.698 13.132a.25.25 0 0 0 .22.368h12.164a.25.25 0 0 0 .22-.368Zm.53 3.996v2.5a.75.75 0 0 1-1.5 0v-2.5a.75.75 0 0 1 1.5 0ZM9 11a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" />
        </svg>

        {/* Message */}
        <span className="text-xs font-body font-medium text-amber-700 flex-1">
          {message}
        </span>

        {/* Dismiss button */}
        <button
          type="button"
          onClick={onDismiss}
          className="text-amber-500 hover:text-amber-700 transition-colors flex-shrink-0"
          aria-label="Dismiss streaming error notification"
        >
          <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M3.72 3.72a.75.75 0 0 1 1.06 0L8 6.94l3.22-3.22a.749.749 0 0 1 1.275.326.749.749 0 0 1-.215.734L9.06 8l3.22 3.22a.749.749 0 0 1-.326 1.275.749.749 0 0 1-.734-.215L8 9.06l-3.22 3.22a.751.751 0 0 1-1.042-.018.751.751 0 0 1-.018-1.042L6.94 8 3.72 4.78a.75.75 0 0 1 0-1.06Z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
